import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Delete1Icon, FolderMoveIcon, SearchIcon } from 'tdesign-icons-react';
import { Button, Input, MessagePlugin, Popconfirm } from 'tdesign-react';

import { LibraryObjectData, ResourceTabType, UserResourceType } from '@/interface/library';
import FolderUpload, { ISpineResult } from '@/material/form-upload/folder';
import { getLimit } from '@/material/form-upload/service';
import { CgiErrorCode } from '@/services/cgi';
import { deleteLibrary, GetListResp, moveGroup, UserResourceDetail } from '@/services/library';
import { isUgc, isWoa } from '@/utils/getApiDomain';

import { picConfigs, publicConfigs, searchPanelConfigs } from '../../config';
import styles from './index.module.less';
import LibraryUpload, { UploadFileProps } from './library';

interface IProps {
  limit: UploadFileProps['limit'];
  list: UserResourceDetail[];
  searchValue: LibraryObjectData;
  type?: UserResourceType;
  checkValue: string[];
  leftList: {
    id: string;
    name: string;
  }[]

  onChangeSearch: (params: {
    searchValue: LibraryObjectData;
    limit: UploadFileProps['limit'];
  }) => void;
  handleUpload: (value?: GetListResp['userResourceList']) => void;
  refreshList: (searchValue: LibraryObjectData) => void;
  onCheckedChange: (value: string, reset?: boolean) => void;
  deleteResource: (ids: string[]) => void;
}

const SearchPanel: React.FC<IProps> = (props) => {
  const {
    leftList = [],
    searchValue,
    limit,
    type,
    checkValue = [],
    handleUpload,
    onChangeSearch,
    refreshList,
    onCheckedChange,
  } = props;

  const [visible, setVisible] = useState(false);
  const [moveVisible, setMoveVisible] = useState(false);
  const [moveId, setMoveId] = useState('');

  useEffect(() => {
    if (moveVisible) {
      setMoveVisible(checkValue?.length > 0);
    }
  }, [checkValue, moveVisible]);

  useEffect(() => {
    if (!moveVisible) {
      setMoveId('');
    }
  }, [moveVisible]);

  useEffect(() => {
    if (visible) {
      setVisible(checkValue?.length > 0);
    }
  }, [visible, checkValue]);

  const handleClickDelete = () => {
    if (checkValue.length > 0 && !type) {
      setVisible(true);
    }
  };

  const handleChangeSearchType = (type: UserResourceType) => {
    if (props?.type) {
      return;
    }
    const tempSearchValue = {
      ...searchValue,
      type,
    };
    onChangeSearch({ searchValue: tempSearchValue, limit: getLimit(searchValue?.type) });
  };

  const handleSearchChange = (value: string) => {
    onChangeSearch({
      searchValue: { ...searchValue, resourceName: value },
      limit,
    });
  };

  const handleDelete = async () => {
    const resourceIds = checkValue || [];

    const { code } = await deleteLibrary({
      materialId: searchValue.materialId,
      tabType: searchValue.tabType,
      resourceIds,
    });
    if (code === CgiErrorCode.NO_ERROR) {
      MessagePlugin.success('素材删除成功');
    } else {
      MessagePlugin.error('素材库删除失败，请稍后重试');
      return;
    }
    refreshList(searchValue);
    setVisible(false);
  };

  const handleCancelDelete = () => {
    onCheckedChange('', true);
    setVisible(false);
  };

  const handleUploadSpine = (_value?: ISpineResult) => {
    handleUpload();
  };

  const handleMove = () => {
    setMoveVisible(true);
  };

  const handleMoveEnd = async () => {
    if (!moveId) {
      MessagePlugin.error('请选择要移动到的目录');
      return;
    }
    const resourceIds = checkValue || [];

    const { code, message } = await moveGroup({
      resourceIds,
      toId: moveId,
    });
    if (code === CgiErrorCode.NO_ERROR) {
      MessagePlugin.success('移动成功');
      setMoveVisible(false);
      refreshList(searchValue);
      return;
    }
    if (code === CgiErrorCode.MoveMaterialPartialFail) {
      MessagePlugin.warning(message || '移动失败');
      setMoveVisible(false);
      refreshList(searchValue);
      return;
    }

    MessagePlugin.error(message || '移动失败');
  };

  const handleMoveCancel = () => {
    setMoveVisible(false);
  };

  const handleChangeMove = (value: string) => {
    setMoveId(value);
  };

  const configs = useMemo(() => {
    if (searchValue?.tabType === ResourceTabType.TAB_TYPE_NOVA
      && searchValue?.type !== UserResourceType.RESOURCE_TYPE_AUDIO) {
      return [];
    }

    switch (searchValue?.tabType) {
      case ResourceTabType.TAB_TYPE_NOVA:
        return publicConfigs;
      case ResourceTabType.TAB_TYPE_PIC:
        return picConfigs;
      default:
        return searchPanelConfigs;
    }
  }, [searchValue?.tabType, searchValue?.type]);

  return (
    <div className={styles.searchPanel}>
      <div className={styles.left}>
        {configs.map((item, index) => (
          <div
            key={index}
            className={classNames(styles.searchItem, {
              [styles.active]: item.value === searchValue.type,
              [styles.disabled]: type && type !== item.value,
            })}
            onClick={() => handleChangeSearchType(item.value)}
          >
            <div className={styles.icon}>{item.icon}</div>
            {item.label}
          </div>
        ))}
      </div>
      <div className={styles.right}>
        {
          type || (searchValue?.tabType === ResourceTabType.TAB_TYPE_NOVA && isUgc)
            || (searchValue?.tabType !== ResourceTabType.TAB_TYPE_NOVA && isWoa) ? null : <div>
            <Popconfirm
              content={`确定删除选中的 ${checkValue?.length} 个素材吗？`}
              destroyOnClose
              placement="bottom"
              showArrow
              theme="warning"
              visible={visible}
              onConfirm={handleDelete}
              onCancel={handleCancelDelete}
            >
              <Button
                disabled={!Boolean(checkValue?.length)}
                style={{ marginRight: 8 }}
                theme="default"
                onClick={handleClickDelete}
                icon={<Delete1Icon />}>
                删除
              </Button>
            </Popconfirm>
          </div>
        }
        {
          type || (searchValue?.tabType === ResourceTabType.TAB_TYPE_NOVA && isUgc)
            || (searchValue?.tabType !== ResourceTabType.TAB_TYPE_NOVA && isWoa) ? null : <div>
            <Popconfirm
              content={
                <div className={styles.moveList}>
                  {
                    leftList?.map(e => (
                      <div
                        key={e?.id}
                        onClick={() => handleChangeMove(e?.id)}
                        className={classNames(styles.item, {
                          [styles.active]: e?.id === moveId,
                        })}
                      >
                        {e?.name || '未命名'}
                      </div>
                    ))
                  }
                </div>}
              destroyOnClose
              placement="bottom"
              showArrow
              theme="warning"
              visible={moveVisible}
              className={styles.move}
              onConfirm={handleMoveEnd}
              onCancel={handleMoveCancel}
            >
              <Button
                disabled={!Boolean(checkValue?.length)}
                style={{ marginRight: 8 }}
                theme="default"
                onClick={handleMove}
                icon={<FolderMoveIcon />}>
                移动到
              </Button>
            </Popconfirm>
          </div>
        }
        {
          (searchValue?.tabType === ResourceTabType.TAB_TYPE_NOVA && isUgc)
            || (searchValue?.tabType !== ResourceTabType.TAB_TYPE_NOVA && isWoa)
            ? null
            : <>
              {searchValue?.type === UserResourceType.RESOURCE_TYPE_SPINE
                ? <FolderUpload
                  {...props}
                  onChange={handleUploadSpine}
                  isMaterial={true}
                  materialValue={searchValue}
                />
                : <LibraryUpload
                  searchValue={searchValue}
                  onChange={handleUpload}
                  limit={getLimit(searchValue?.type, searchValue?.tabType)}
                  multiple={false}
                />}
            </>
        }
      </div>
      <div className={styles.searchInput}>
        <Input
          suffixIcon={<SearchIcon />}
          placeholder="请输入"
          value={searchValue.resourceName}
          onChange={handleSearchChange}
          style={{ width: 200 }}
        />
      </div>
    </div>
  );
};

export default SearchPanel;
